import type { CatalogResultCard } from '../catalog/types'

/**
 * UID-36 demo hardcoding: the sourcing-agent chip prompts
 * (`CatalogBrowsePage.tsx`'s `SOURCING_SUGGESTIONS`) describe intents the
 * real `/catalog/search` endpoint can't resolve ("Find a 20mm linear guide in
 * stock" isn't a keyword match) — so for the demo, each exact prompt gets a
 * canned 3-item shortlist instead of a live search. Keyed by the verbatim
 * prompt text; `useAgentThread.ask` checks this before calling the real
 * service.
 */

const EACH = { code: 'EA', kind: 'each' } as const

function catalogItem(params: {
  readonly id: string
  readonly name: string
  readonly catalogSku: string
  readonly amount: string
  readonly note: string
  readonly category: string
  readonly imageRef?: string
}): CatalogResultCard {
  return {
    sourceable: {
      id: params.id,
      kind: 'catalog',
      uom: { saleUom: EACH, pricingUom: EACH },
      name: params.name,
      catalogSku: params.catalogSku,
      configAttributes: {},
      indicativePrice: {
        amount: params.amount,
        currency: 'USD',
        basis: 'EA',
        provenance: 'catalog',
      },
      provenance: 'misumi_standard',
      imageRef: params.imageRef ?? null,
    },
    availability: {
      availableQuantity: null,
      note: params.note,
      status: 'in_stock',
      leadTimeDays: null,
    },
    category: params.category,
  } as CatalogResultCard
}

/**
 * The fastener shortlist stands in for ALL FOUR mocked prompts for now
 * (temporary — the other three intents each need their own real catalog SKUs,
 * which are pending a manual catalog update; see the ticket). Once those SKUs
 * exist, give each prompt its own entry instead of sharing this one.
 */
const FASTENERS_FOR_A_6061_BRACKET: readonly CatalogResultCard[] = [
  catalogItem({
    id: '00000000-0000-0000-0000-000000000101',
    // No `material` field exists on the wire (Sourceable/CatalogResultCard),
    // and `catalogSku` is a real identifier that must stay exactly the real
    // SKU — never display text. Material folds into the name instead.
    name: 'Socket Head Cap Screw M8x30, Aluminum',
    catalogSku: 'CSHHL-STTALU-M8-30',
    amount: '1.20',
    note: 'In stock · Ships in 1 day',
    category: 'Fasteners',
    imageRef:
      'https://content.misumi-ec.com/image/upload/v1/p/jp/product/series/110302280540/110302280540_001_20230801115836.jpg',
  }),
  catalogItem({
    id: '00000000-0000-0000-0000-000000000102',
    name: 'Hex Nut M8, Stainless Steel',
    catalogSku: 'HXN-310S-M8-30',
    amount: '0.85',
    note: 'In stock · Ships in 1 day',
    category: 'Fasteners',
    imageRef: 'https://us.misumi-ec.com/linked/material/mech/MSM1/PHOTO/110302603510_001.JPG',
  }),
  catalogItem({
    id: '00000000-0000-0000-0000-000000000103',
    name: 'Cap Bolt Bracket Fastener Set, Steel',
    catalogSku: 'CBSA6-8',
    amount: '2.50',
    note: 'In stock · Ships in 1 day',
    category: 'Fasteners',
    imageRef:
      'https://content.misumi-ec.com/image/upload/v1/p/jp/product/series/110300243970/110300243970_20210115122526.jpg',
  }),
]

const MOCKED_AGENT_SHORTLISTS: ReadonlyMap<string, readonly CatalogResultCard[]> = new Map([
  ['Find a 20mm linear guide in stock', FASTENERS_FOR_A_6061_BRACKET],
  ['Bearing for a 25mm shaft', FASTENERS_FOR_A_6061_BRACKET],
  ['Fasteners for a 6061 bracket', FASTENERS_FOR_A_6061_BRACKET],
  ['Spec parts for an XY gantry', FASTENERS_FOR_A_6061_BRACKET],
])

/** Returns the canned shortlist for an exact mocked-prompt match, or `null`
 * for any other question (which falls through to the real search). */
export function getMockedAgentShortlist(question: string): readonly CatalogResultCard[] | null {
  return MOCKED_AGENT_SHORTLISTS.get(question.trim()) ?? null
}
